import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ProviderLayout from '../../components/ProviderLayout';
import { apiFetch, API_BASE_URL } from '../../api';

const PRIMARY = '#4F46E5';

// Force absolute https so avatars load reliably.
function httpsUrl(u) {
  if (!u) return null;
  if (u.startsWith('http://')) return 'https://' + u.slice(7);
  if (u.startsWith('/')) return API_BASE_URL + u;
  return u;
}

export default function ProviderProfilePage() {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();
  const user = auth?.user || {};

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch('/api/provider/profile', auth.token);
      if (!res.ok) throw new Error();
      setProfile(await res.json());
    } catch {
      // Profile detail is optional — still show the basic account info below.
      setProfile(null);
      setError('Profil tafsilotlarini yuklashda xatolik yuz berdi.');
    } finally {
      setLoading(false);
    }
  }, [auth.token]);

  useEffect(() => { load(); }, [load]);

  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ') || 'Usta';
  const avatar = httpsUrl(user.avatarUrl || user.imageUrl || (profile && (profile.avatarUrl || profile.imageUrl)));

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <ProviderLayout title="Profil">
      <div style={s.head}>
        {avatar ? (
          <img src={avatar} alt={fullName} style={s.avatar} />
        ) : (
          <div style={{ ...s.avatar, ...s.avatarEmpty }}>{fullName.charAt(0).toUpperCase()}</div>
        )}
        <div style={s.name}>{fullName}</div>
        {user.phone && <div style={s.phone}>{user.phone}</div>}
      </div>

      {loading ? (
        <div style={s.center}>Yuklanmoqda...</div>
      ) : (
        <>
          {error && <div style={s.info}>{error}</div>}
          <div style={s.block}>
            <Row label="Telefon" value={user.phone} />
            <Row label="Manzil" value={user.address} />
            {profile && <Row label="Bio" value={profile.bio} />}
            {profile && <Row label="Tajriba (yil)" value={profile.experienceYears} />}
            {profile && <Row label="Shahar" value={profile.serviceAreaCity} />}
            {profile && <Row label="Tuman" value={profile.serviceAreaDistrict} />}
          </div>
        </>
      )}

      <button style={s.logout} onClick={handleLogout}>Chiqish</button>
    </ProviderLayout>
  );
}

function Row({ label, value }) {
  return (
    <div style={s.row}>
      <span style={s.rowLabel}>{label}</span>
      <span style={s.rowValue}>
        {value === null || value === undefined || value === '' ? '—' : String(value)}
      </span>
    </div>
  );
}

const s = {
  head: { textAlign: 'center', padding: '16px 0 20px' },
  avatar: {
    width: '84px',
    height: '84px',
    borderRadius: '50%',
    objectFit: 'cover',
    margin: '0 auto 12px',
    display: 'block',
    background: '#EEF2FF',
  },
  avatarEmpty: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '34px',
    fontWeight: '800',
    color: PRIMARY,
  },
  name: { fontSize: '19px', fontWeight: '700', color: 'var(--slate-900)' },
  phone: { fontSize: '14px', color: '#64748B', marginTop: '4px' },
  center: { textAlign: 'center', padding: '32px', color: 'var(--slate-400)' },
  info: {
    background: '#FFFBEB',
    border: '1px solid #FCD34D',
    borderRadius: '10px',
    padding: '10px 14px',
    color: '#92400E',
    fontSize: '13px',
    marginBottom: '12px',
  },
  block: {
    background: 'var(--surface)',
    borderRadius: '16px',
    border: '1px solid #E2E8F0',
    padding: '4px 16px',
    marginBottom: '20px',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '12px 0',
    borderBottom: '1px solid #F1F5F9',
    fontSize: '14px',
  },
  rowLabel: { color: '#64748B' },
  rowValue: { color: 'var(--slate-900)', fontWeight: '600', textAlign: 'right' },
  logout: {
    width: '100%',
    padding: '13px',
    background: 'var(--danger-tint)',
    color: 'var(--danger)',
    border: '1px solid #FECACA',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
  },
};
